import React from "react";
import styled from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";
import { IoCheckmark, IoLockClosed } from "react-icons/io5";

import history from "../../../../history";
import { analytics } from "MVVM/Analytics";
import {
  TIER_DATA,
  TIER_HIGHLIGHTS,
} from "MVVM/Views/Pricing/Components/PricingCards/features";
import { TTierKey } from "MVVM/Views/Pricing/Components/PricingCards/types";
import {
  IHighlightItem,
  IHighlightSection,
} from "MVVM/Views/Pricing/Components/PricingCards/types";
import { ReactComponent as ProIconSVG } from "../../../Assets/Avatar/Icons/pro.svg";
import { ReactComponent as PlatinumIconSVG } from "../../../Assets/Avatar/Icons/platinum.svg";
import { ReactComponent as EssentialsIconSVG } from "../../../Assets/Avatar/Icons/essentials.svg";

const TIER_ACCENT: Record<string, string> = {
  ESSENTIALS: "#4ebd94",
  PRO: "#00c8dc",
  CREATOR_PLAT: "#9a58d1",
};

const getHeaderGradient = (tierKey: string) => {
  switch (tierKey) {
    case "ESSENTIALS":
      return "linear-gradient(135deg, rgba(78, 189, 148, 0.25) 0%, transparent 60%)";
    case "PRO":
      return "linear-gradient(135deg, rgba(0, 200, 220, 0.25) 0%, transparent 60%)";
    case "CREATOR_PLAT":
      return "linear-gradient(135deg, rgba(154, 88, 209, 0.25) 0%, transparent 60%)";
    default:
      return "transparent";
  }
};

const getButtonGradient = (tierKey: string) => {
  switch (tierKey) {
    case "ESSENTIALS":
      return "linear-gradient(135deg, #4ebd94 0%, #3da87f 100%)";
    case "PRO":
      return "linear-gradient(135deg, #00c8dc 0%, #00a8b8 100%)";
    case "CREATOR_PLAT":
      return "linear-gradient(135deg, #9a58d1 0%, #7b46a8 100%)";
    default:
      return "linear-gradient(135deg, #00c8dc 0%, #00a8b8 100%)";
  }
};

const getTierIcon = (tierKey: string) => {
  switch (tierKey) {
    case "ESSENTIALS":
      return <EssentialsIconSVG />;
    case "PRO":
      return <ProIconSVG />;
    case "CREATOR_PLAT":
      return <PlatinumIconSVG />;
    default:
      return null;
  }
};

const formatHighlight = (text: string) => {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
  );
};

interface SubscriptionPaywallProps {
  featureName: string;
  requiredTierKey: TTierKey;
  onClose: () => void;
}

export const SubscriptionPaywall = ({
  featureName,
  requiredTierKey,
  onClose,
}: SubscriptionPaywallProps) => {
  const tierData = TIER_DATA.find((t) => t.key === requiredTierKey);
  const tierHighlights = TIER_HIGHLIGHTS[requiredTierKey];
  const accent = TIER_ACCENT[requiredTierKey] || "#00c8dc";

  if (!tierData) return null;

  const handleUpgradeClick = () => {
    analytics.track("Subscription Paywall CTA Clicked", {
      feature: featureName,
      requiredTier: requiredTierKey,
    });
    onClose();
    history.push("/upgrade");
  };

  const handleSeeAllPlans = () => {
    analytics.track("Subscription Paywall See All Plans", {
      feature: featureName,
      requiredTier: requiredTierKey,
    });
    onClose();
    history.push("/upgrade");
  };

  const sections = tierHighlights?.sections || [];

  return (
    <Container onClick={(e) => e.stopPropagation()}>
      <Header $tierKey={requiredTierKey}>
        <BackgroundIcon $tierKey={requiredTierKey}>
          {getTierIcon(requiredTierKey)}
        </BackgroundIcon>

        <HeaderContent>
          <LockRow>
            <LockIcon />
            <FeatureName>{featureName}</FeatureName>
          </LockRow>

          <UnlockHeading>
            Unlock with <TierAccent $color={accent}>{tierData.name}</TierAccent>
          </UnlockHeading>

          <PriceRow>
            <PriceText>{tierData.pricing.annual}</PriceText>
            <PriceUnit>/month</PriceUnit>
            {tierData.pricing.discount && (
              <Discount>{tierData.pricing.discount}</Discount>
            )}
          </PriceRow>
        </HeaderContent>
      </Header>

      <Body>
        {sections.map((section: IHighlightSection, sectionIndex: number) => (
          <HighlightSection key={sectionIndex}>
            <SectionTitle>{section.title}</SectionTitle>
            <FeatureList>
              {section.items.map(
                (item: string | IHighlightItem, itemIndex: number) => {
                  const isObject = typeof item === "object";
                  const text = isObject ? item.text : item;
                  return (
                    <FeatureItem key={itemIndex}>
                      <CheckIcon $color={accent} />
                      <FeatureText>{formatHighlight(text)}</FeatureText>
                    </FeatureItem>
                  );
                },
              )}
            </FeatureList>
          </HighlightSection>
        ))}

        <UpgradeButton $tierKey={requiredTierKey} onClick={handleUpgradeClick}>
          Upgrade to {tierData.name}
        </UpgradeButton>

        <SeeAllPlans onClick={handleSeeAllPlans}>See all plans</SeeAllPlans>
      </Body>
    </Container>
  );
};

const Container = styled(Box)`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.25),
    0 2px 4px rgba(0, 0, 0, 0.15);
  max-width: 400px;
  overflow: hidden;
  width: 90vw;
`;

const Header = styled(Box)<{ $tierKey: string }>`
  background: ${({ $tierKey }) => getHeaderGradient($tierKey)};
  padding: 24px;
  position: relative;
  overflow: hidden;
`;

const BackgroundIcon = styled(Box)<{ $tierKey: string }>`
  left: -30px;
  opacity: 0.12;
  position: absolute;
  top: -40px;
  z-index: 0;

  svg {
    height: 120px;
    width: 120px;
    -webkit-mask: linear-gradient(
      to right,
      black 0%,
      black 40%,
      transparent 85%
    );
    mask: linear-gradient(to right, black 0%, black 40%, transparent 85%);
  }
`;

const HeaderContent = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  position: relative;
  z-index: 1;
`;

const LockRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const LockIcon = styled(IoLockClosed)`
  color: ${({ theme }) => theme.colors.chalk};
  height: 16px;
  width: 16px;
`;

const FeatureName = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  font-weight: 500;
`;

const UnlockHeading = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 22px;
  font-weight: 700;
`;

const TierAccent = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
`;

const PriceRow = styled(Flex)`
  align-items: baseline;
  gap: 4px;
`;

const PriceText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
`;

const PriceUnit = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  font-weight: 500;
`;

const Discount = styled(Text)`
  background: ${({ theme }) => theme.colors.envyAlpha10};
  border-radius: 20px;
  color: ${({ theme }) => theme.colors.envy};
  font-size: 11px;
  font-weight: 600;
  margin-left: 8px;
  padding: 3px 10px;
`;

const Body = styled(Box)`
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding: 24px;
`;

const HighlightSection = styled(Box)`
  margin-bottom: 16px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
  text-transform: uppercase;
`;

const FeatureList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FeatureItem = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const CheckIcon = styled(IoCheckmark)<{ $color: string }>`
  color: ${({ $color }) => $color};
  min-height: 18px;
  min-width: 18px;
`;

const FeatureText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 500;

  strong {
    color: ${({ theme }) => theme.colors.ice};
    font-weight: 700;
  }
`;

const UpgradeButton = styled.button<{ $tierKey: string }>`
  background: ${({ $tierKey }) => getButtonGradient($tierKey)};
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  margin-top: 24px;
  padding: 12px 20px;
  transition: filter 0.2s ease;
  width: 100%;

  &:hover {
    filter: brightness(1.1);
  }
`;

const SeeAllPlans = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-size: 14px;
  margin-top: 12px;
  text-align: center;
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.colors.chalkAlpha40};
  transition: color 0.2s ease;
  width: 100%;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;
